import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import {
  capture,
  capturePageview,
  getScrollDepthThresholdsCrossed,
  initAnalytics,
  isScrollTrackedRoute,
  sanitizeRouteKey,
} from './index';

const AnalyticsRouteTracker = () => {
  const location = useLocation();
  const emittedScrollThresholdsRef = useRef(new Set());

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    const routeKey = `${location.pathname}${location.search || ''}`;
    capturePageview(routeKey);
    emittedScrollThresholdsRef.current = new Set();
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    if (!isScrollTrackedRoute(location.pathname)) {
      return undefined;
    }

    let frameId = null;
    const routeKey = `${location.pathname}${location.search || ''}`;
    const sanitizedRoute = sanitizeRouteKey(routeKey);

    const measureAndCapture = () => {
      frameId = null;
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop || document.body.scrollTop || 0;
      const scrollableHeight = Math.max(
        0,
        doc.scrollHeight,
        document.body.scrollHeight,
        doc.offsetHeight,
        document.body.offsetHeight,
      ) - window.innerHeight;

      if (scrollableHeight <= 0) {
        return;
      }

      const currentPercent = Math.min(100, Math.round((scrollTop / scrollableHeight) * 100));
      const crossed = getScrollDepthThresholdsCrossed({
        currentPercent,
        emittedThresholds: emittedScrollThresholdsRef.current,
      });

      crossed.forEach((threshold) => {
        emittedScrollThresholdsRef.current.add(threshold);
        capture('scroll_depth_reached', {
          ...sanitizedRoute,
          threshold,
          scroll_percent: currentPercent,
        });
      });
    };

    const handleScroll = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(measureAndCapture);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    measureAndCapture();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [location.pathname, location.search]);

  return null;
};

export default AnalyticsRouteTracker;
